import type {ReactElement} from 'react';
import {useContext, useRef, useState} from 'react';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {DesignSelector} from '@Components/design-selector';
import type {CreateDesignFromMediaModalOpts} from '@Components/design-selector/design-selector.types';
import {DesignSelectorFrameVariant, DesignSelectorTab} from '@Components/design-selector/design-selector.types';
import type {DesignSelection} from '@Components/social-media/social-media.types';
import {
  getDesignGalleryHeightForSelectDesignStep,
  getFilteredDesigns,
  getSelectedDesignCount,
  getWizardUploadDesignsModalOpts,
  WIZARD_UPLOAD_MEDIA_GA4_CALLBACKS,
} from '@Libraries/social-media-wizard-design-library';
import {MAX_UPLOADS_TO_DESIGNS} from '@Libraries/social-media-wizard-design-library.types';
import {getStartingURIForGallery} from '@Libraries/social-media-wizard-step-library';
import {
  disableNextButton,
  enableNextButton,
  updateDesignsToPublishAndTriggerNext,
  updateDesignSelectorTab,
  updateIsTemplateSelected,
  updateTriggerNext,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {GA4EventName, trackPublishingGA4Events} from '@Libraries/ga-events';
import {noop} from '@Utils/general.util';
import {useAppDispatch} from '@/hooks';
import {DesignSelectionSummaryBar} from './components/design-selection-summary-bar/design-selection-summary-bar';
import type {WizardDesignSelectorPanelProps} from './wizard-design-selector-panel.types';
import {UPLOADED_DESIGNS_NEXT_TRIGGER_DELAY_MS} from './wizard-design-selector-panel.types';

export function WizardDesignSelectorPanel({
  designsToPublish,
  onDesignListChange,
  onDesignsStaged,
  designSelectorTab,
  galleryStartingURI,
}: WizardDesignSelectorPanelProps): ReactElement {
  const dispatch = useAppDispatch();
  const {isSmallScreen, isExtraSmallScreen} = useContext(SocialMediaWizardContainerQueriesContext);
  const isMobileLayout = Boolean(isSmallScreen) || Boolean(isExtraSmallScreen);
  const designSelectorRef = useRef<HTMLDivElement>(null);
  const [isDesignSelectorLoading, setDesignSelectorIsLoading] = useState(false);
  const onUploadMediaDesignsCallbackRef = useRef<(designs: DesignSelection[]) => void>(noop);

  onUploadMediaDesignsCallbackRef.current = (designs: DesignSelection[]): void => {
    const mergedDesigns = [...designsToPublish, ...getFilteredDesigns(designs, MAX_UPLOADS_TO_DESIGNS)];
    onDesignsStaged(mergedDesigns);
    setDesignSelectorIsLoading(false);
    updateTriggerDelayedNext();
  };

  const updateTriggerDelayedNext = (): void => {
    setTimeout((): void => {
      dispatch(updateTriggerNext());
    }, UPLOADED_DESIGNS_NEXT_TRIGGER_DELAY_MS);
  };

  const onTabSelected = (newTab: DesignSelectorTab): void => {
    if (newTab === DesignSelectorTab.GALLERY_TEMPLATES) {
      handleDesignListChange([]);
    }

    dispatch(updateDesignSelectorTab(newTab));
  };

  const handleDesignListChange = (designs: DesignSelection[]): void => {
    if (designs.length === 0) {
      dispatch(disableNextButton());
    } else {
      dispatch(enableNextButton());
    }

    onDesignListChange(designs);
  };

  const deselectAllDesigns = (): void => {
    handleDesignListChange([]);
  };

  const handleGalleryTemplatesSelected = (templateDesigns: DesignSelection[]): void => {
    trackPublishingGA4Events(GA4EventName.SOCIALWIZARD_TEMPLATESELECTED);
    dispatch(updateIsTemplateSelected(true));
    onDesignsStaged(templateDesigns);
    dispatch(updateDesignsToPublishAndTriggerNext(templateDesigns));
  };

  const getStartDesignFromUploadBtnOpts = (): CreateDesignFromMediaModalOpts => {
    return getWizardUploadDesignsModalOpts({
      ...WIZARD_UPLOAD_MEDIA_GA4_CALLBACKS,
      onDesignsCreated: (designs: DesignSelection[]): void => {
        onUploadMediaDesignsCallbackRef.current(designs);
      },
      onPosterCreationsStarted: (): void => {
        setDesignSelectorIsLoading(true);
        dispatch(disableNextButton());
      },
    });
  };

  const selectedDesignCount = getSelectedDesignCount(designsToPublish);

  const getInlineSelectionSummaryCount = (): number => {
    return isMobileLayout ? 0 : selectedDesignCount;
  };

  const getMobileSelectionSummaryBar = (): ReactElement | null => {
    if (!isMobileLayout) {
      return null;
    }

    return <DesignSelectionSummaryBar selectedDesignCount={selectedDesignCount} onDeselectAll={deselectAllDesigns} />;
  };

  const renderDesignSelector = (): ReactElement => {
    return (
      <DesignSelector
        containerRef={designSelectorRef}
        initialDesignSelections={designsToPublish}
        onGalleryTemplateSelectionCallback={handleGalleryTemplatesSelected}
        onDesignsSelection={handleDesignListChange}
        designSelectorTab={designSelectorTab}
        onTabSelected={onTabSelected}
        frameVariant={DesignSelectorFrameVariant.SOCIAL_MEDIA_WIZARD}
        startDesignFromMediaModalBtnOpts={getStartDesignFromUploadBtnOpts()}
        isLoading={isDesignSelectorLoading}
        designGalleryStartingURI={galleryStartingURI ?? getStartingURIForGallery()}
        designGalleryHeight={getDesignGalleryHeightForSelectDesignStep(isMobileLayout)}
        scrollContainerEl={window}
        selectedDesignCount={getInlineSelectionSummaryCount()}
        onDeselectAll={deselectAllDesigns}
      />
    );
  };

  return (
    <>
      {renderDesignSelector()}
      {getMobileSelectionSummaryBar()}
    </>
  );
}
